// src/components/common/CategoryCard.jsx
import React from "react";
import { Link as RouterLink } from "react-router-dom";
import pdfIcon from "../../assets/pdf.png";

const PlayBulletIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 20 20"
    fill="currentColor"
    className="w-7 h-7 mr-3 md:mr-4 flex-shrink-0 text-gray-300 hover:text-white"
  >
    <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
  </svg>
);

const CategoryCard = ({ category, type = "product" }) => {
  if (!category) return null;
  const { name, logo } = category;
  const logoUrl = logo?.url ? `${logo.url}` : "/placeholder-logo.png";
  const itemsToList =
    type === "download" ? category.downloads_files : category.list;
  const itemList = itemsToList || [];

  const cardRoundedClass = "rounded-[20px]";
  const shadowColor = "rgba(0, 0, 0, 0.2)";

  const fileSize = (size) => {
    if (size < 1024) return `${size.toFixed(0)} KB`;
    return `${(size / 1024).toFixed(2)} MB`;
  };

  return (
    <div
      className={`flex flex-col md:flex-row my-6 ${cardRoundedClass} overflow-hidden`}
      style={{
        boxShadow: `
          0 0 0 2px #0A285F, 
          0 30px 50px -10px ${shadowColor},
          0 15px 25px -5px ${shadowColor} 
        `,
      }}
    >
      {/* Logo */}
      <div
        className={`md:w-[280px] lg:w-[320px] flex-shrink-0 bg-white p-6 md:p-8 flex items-center justify-center 
                   border-b-0 md:border-r-0`}
      >
        <img
          src={logoUrl}
          alt={logo?.alternativeText || name || "Category Logo"}
          className="max-h-20 md:max-h-28 lg:max-h-32 w-auto object-contain"
        />
      </div>

      {/* List */}
      <div
        className={`flex-grow p-6 md:p-8 bg-[#0A285F] ${cardRoundedClass} text-white`}
      >
        {itemList.length > 0 ? (
          <ul className="space-y-3 md:space-y-4">
            {itemList.map((item) => {
              let itemTitle,
                itemUrl,
                itemSize = null;
              let isDownloadLink = false;

              if (type === "download" && item.download_file) {
                itemTitle = item.download_title || item.download_file.name;
                itemUrl = item.download_file.url
                  ? `${item.download_file.url}`
                  : "#";
                itemSize = item.download_file.size;
                isDownloadLink = true;
              } else if (type === "product" && item.title) {
                itemTitle = item.title;
                itemUrl = item.url || "#";
              } else {
                return null;
              }

              const linkContent = (
                <>
                  {isDownloadLink && (
                    <img
                      src={pdfIcon}
                      className="w-8 mr-3 md:mr-4 flex-shrink-0 text-gray-300 hover:text-white"
                    />
                  )}
                  {!isDownloadLink && <PlayBulletIcon />}{" "}
                  <span className="truncate text-lg md:text-xl lg:text-2xl">
                    {itemTitle}
                  </span>{" "}
                  {isDownloadLink && itemSize && (
                    <span className="ml-2 mt-1 text-sm text-gray-300 whitespace-nowrap md:hidden xl:flex">
                      ({fileSize(itemSize)})
                    </span>
                  )}
                </>
              );

              const linkClasses =
                "text-base md:text-lg text-gray-100 group-hover:text-white group-hover:underline transition-colors duration-150 flex items-center";

              return (
                <li key={item.id || itemTitle} className="group">
                  {isDownloadLink ? (
                    <a
                      href={itemUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={linkClasses}
                    >
                      {linkContent}
                    </a>
                  ) : (
                    <RouterLink to={itemUrl} className={linkClasses}>
                      {linkContent}
                    </RouterLink>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-gray-300">
            {type === "download"
              ? "No download files in this category."
              : "No products listed in this category."}
          </p>
        )}
      </div>
    </div>
  );
};

export default CategoryCard;
